import { AppLayout } from '@/components/app-layout';
import { AuthCheck } from '@/components/auth-check.component';
import { FeatureGate } from '@/components/feature-gate.component';
import { Head } from '@/components/head.component';
import { Feature } from '@/constants/features';
import { AgentsContainer } from '@/containers/agents/agents-container.component';
import { SKILLS_VIEWS } from '@/containers/agents/writing-skills.util';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

// Tab values accepted in the URL search.
//
//   Agents:        agents | plays | skills | knowledge | profile
//   New (CLE-321): catalog | profile | templates
//   Legacy:        icp | custom-ai-columns
//
// Legacy values are kept in the enum so existing in-app links and external
// links (Slack, emails) keep working through the transition. AgentsContainer
// maps legacy values to the new tabs:
//   'icp' / 'custom-ai-columns' -> the Templates sub-tab.
const agentsSearchSchema = z.object({
  tab: z
    .enum([
      'agents',
      'plays',
      'skills',
      'knowledge',
      'catalog',
      'profile',
      'templates',
      'icp',
      'custom-ai-columns',
    ])
    .optional()
    // .catch: an unknown tab (stale link, typo) falls back instead of
    // throwing, which crashed the whole page ("This page couldn't load").
    .catch(undefined),
  /** The Skills tab's view (`?tab=skills&view=writing`); unknown falls back
   *  to All. */
  view: z.enum(SKILLS_VIEWS).optional().catch(undefined),
  /** When true, AgentsContainer renders the OnboardingSetup card in
   *  re-run mode regardless of onboarding_completed_at. Driven by the
   *  Profile tab's "Re-run profile research" button. A boolean, not
   *  'true': the router JSON-parses search values, so a typed or pasted
   *  `?rerun=true` arrives as a boolean and a string enum dropped it. */
  rerun: z.boolean().optional().catch(undefined),
});

export const Route = createFileRoute('/agents/')({
  component: AgentsPage,
  validateSearch: agentsSearchSchema,
});

function AgentsPage() {
  const { tab, view, rerun } = Route.useSearch();

  return (
    <>
      <Head heading='Agents' />
      <AuthCheck>
        <AppLayout variant='full-bleed'>
          <FeatureGate feature={Feature.AGENTS}>
            <AgentsContainer
              initialTab={tab}
              initialView={view}
              rerun={rerun}
            />
          </FeatureGate>
        </AppLayout>
      </AuthCheck>
    </>
  );
}
